import * as React from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Grid,
} from "@mui/material";
import { ExpandMore, HelpOutline, ShoppingCart, Payment, Inventory } from "@mui/icons-material";

const helpSections = [
  {
    icon: <ShoppingCart />,
    title: "¿Cómo agregar productos al carrito?",
    content: (
      <>
        <Typography sx={{ mb: 2, color: "#555", lineHeight: 1.7 }}>
          Una vez que ingresas al sistema se muestra una pantalla con todos los productos disponibles.
          Cada producto cuenta con su precio final ya aplicándose el máximo descuento del <strong>40%</strong>.
        </Typography>
        <Typography sx={{ mb: 2, color: "#555", lineHeight: 1.7 }}>
          Cada producto cuenta con botones <strong>+</strong> y <strong>-</strong> para sumar o restar cantidad.
          Para agregar productos al carrito, haz clic sobre el botón <strong>+</strong> o el botón "Agregar".
        </Typography>
        <Typography sx={{ color: "#555", lineHeight: 1.7 }}>
          Para ver los productos en tu carrito, haz clic en el ícono del carrito en la esquina superior derecha.
        </Typography>
      </>
    ),
  },
  {
    icon: <Inventory />,
    title: "¿Cómo actualizo la cantidad de productos?",
    content: (
      <>
        <Typography sx={{ mb: 2, color: "#555", lineHeight: 1.7 }}>
          Una vez en el carrito, verás una ventana con el detalle de productos agregados,
          incluyendo <strong>Subtotal, Descuento y Total</strong>.
        </Typography>
        <Typography sx={{ mb: 2, color: "#555", lineHeight: 1.7 }}>
          El número en color <span style={{ color: "#22c55e", fontWeight: 600 }}>verde</span> indica
          el stock disponible. Puedes modificar la cantidad directamente en el campo de texto.
        </Typography>
        <Typography sx={{ color: "#555", lineHeight: 1.7 }}>
          Al modificar la cantidad, el total se recalcula automáticamente junto con el descuento.
        </Typography>
      </>
    ),
  },
  {
    icon: <HelpOutline />,
    title: "¿Cómo termino mi compra?",
    content: (
      <>
        <Typography sx={{ mb: 2, color: "#555", lineHeight: 1.7 }}>
          Para concluir una compra, es necesario seleccionar una <strong>dirección de envío</strong>.
        </Typography>
        <Typography sx={{ color: "#555", lineHeight: 1.7 }}>
          Una vez seleccionada la dirección, haz clic en el botón <strong>"Terminar Compra"</strong>.
        </Typography>
      </>
    ),
  },
  {
    icon: <Payment />,
    title: "¿Cómo pago mi compra?",
    content: (
      <>
        <Typography sx={{ mb: 2, color: "#555", lineHeight: 1.7 }}>
          Una vez concluida la compra, serás redirigido a <strong>"Mis Pedidos"</strong> donde
          podrás consultar todos tus pedidos organizados por status:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2, color: "#555" }}>
          <li>Pedidos Pendientes de pago</li>
          <li>Pedidos en validación de pago</li>
          <li>Pedidos en preparación</li>
          <li>Pedidos Enviados</li>
          <li>Pedidos Cancelados</li>
        </Box>
        <Typography sx={{ color: "#555", lineHeight: 1.7 }}>
          Para pagar, busca el botón <strong>"Subir Comprobante"</strong> y adjunta tu comprobante
          de transferencia con el folio y cantidad depositada. Tu pago pasará por revisión antes de aprobarse.
        </Typography>
      </>
    ),
  },
];

export default function Help() {
  const [expanded, setExpanded] = React.useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #FFF5F8 0%, #fff 100%)",
        py: 6,
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            sx={{
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 800,
              background: "linear-gradient(135deg, #801F56 0%, #9A457F 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              mb: 1,
            }}
          >
            💅 Centro de Ayuda
          </Typography>
          <Typography
            sx={{
              fontSize: "1.1rem",
              color: "#666",
              maxWidth: "500px",
              margin: "0 auto",
            }}
          >
            Encuentra respuestas a las preguntas más frecuentes sobre tu experiencia de compra
          </Typography>
        </Box>

        {/* Accordion Cards */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {helpSections.map((section, index) => (
            <Accordion
              key={index}
              expanded={expanded === `panel${index}`}
              onChange={handleChange(`panel${index}`)}
              sx={{
                borderRadius: "16px !important",
                overflow: "hidden",
                boxShadow: expanded === `panel${index}`
                  ? "0 10px 40px rgba(128, 31, 86, 0.15)"
                  : "0 2px 10px rgba(0,0,0,0.05)",
                border: "2px solid",
                borderColor: expanded === `panel${index}` ? "#FAA5B7" : "transparent",
                transition: "all 0.3s ease",
                "&:before": { display: "none" },
                "&:hover": {
                  boxShadow: "0 10px 40px rgba(128, 31, 86, 0.1)",
                },
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMore
                    sx={{
                      color: "#801F56",
                      fontSize: "1.5rem",
                    }}
                  />
                }
                sx={{
                  py: 1,
                  px: 3,
                  "& .MuiAccordionSummary-content": {
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #FAA5B7 0%, #F0C9D5 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#801F56",
                    flexShrink: 0,
                  }}
                >
                  {section.icon}
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.95rem", md: "1.1rem" },
                    color: "#333",
                  }}
                >
                  {section.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  px: 3,
                  pb: 3,
                  pt: 0,
                  ml: { md: 7.5 },
                }}
              >
                {section.content}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Contact CTA */}
        <Box
          sx={{
            mt: 6,
            p: 4,
            borderRadius: "20px",
            background: "linear-gradient(135deg, #801F56 0%, #9A457F 100%)",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "#fff",
              mb: 1,
            }}
          >
            ¿No encontraste lo que buscabas?
          </Typography>
          <Typography sx={{ color: "#FAA5B7", mb: 2 }}>
            Nuestro equipo está aquí para ayudarte
          </Typography>
          <Box
            component="a"
            href="/contacto"
            sx={{
              display: "inline-block",
              background: "#fff",
              color: "#801F56",
              px: 4,
              py: 1.5,
              borderRadius: "30px",
              fontWeight: 700,
              textDecoration: "none",
              transition: "all 0.2s",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              },
            }}
          >
            💌 Contáctanos
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
